import * as XLSX from "xlsx";

// Reserved key used when a warehouse cylinder has no ProductId to join on.
export const UNKNOWN_PRODUCT_ID = "__no_product_id__";

export function asText(value) {
  return value == null ? "" : String(value).trim();
}

// Canonical "available to a customer" rule.
// Physically in the warehouse, not dispatched, and not allocated to an order.
// This is the single source of truth for availability in the browser report.
export function isAvailableForCustomer(record) {
  return (
    record?.Stock === true &&
    record?.Dispatch !== true &&
    record?.Allocated !== true
  );
}

// Count available cylinders grouped by ProductId (cyl_info doc id).
export function computeAvailableCounts(warehouseRecords) {
  const counts = new Map();
  for (const record of warehouseRecords || []) {
    if (!isAvailableForCustomer(record)) continue;
    const productId = asText(record?.ProductId) || UNKNOWN_PRODUCT_ID;
    counts.set(productId, (counts.get(productId) || 0) + 1);
  }
  return counts;
}

// Build a lookup of cyl_info docs keyed by doc id.
// cyl_info only reliably carries productCode + productDescription.
export function buildCylInfoMap(cylInfoDocuments) {
  const map = new Map();
  for (const cyl of cylInfoDocuments || []) {
    const id = asText(cyl?.id);
    if (!id) continue;
    map.set(id, {
      productCode: asText(cyl?.productCode),
      productDescription: asText(cyl?.productDescription),
    });
  }
  return map;
}

// Join available counts to cyl_info descriptions.
//   rows:      customer-facing rows we can describe, sorted by description
//   unmatched: available stock we could not describe (internal follow-up only)
export function buildCustomerStockRows({ counts, cylInfoMap }) {
  const rows = [];
  const unmatched = [];

  for (const [productId, available] of counts || new Map()) {
    const info =
      productId === UNKNOWN_PRODUCT_ID ? null : cylInfoMap?.get(productId);

    if (!info || (!info.productCode && !info.productDescription)) {
      unmatched.push({
        productId: productId === UNKNOWN_PRODUCT_ID ? "" : productId,
        productCode: info?.productCode || "",
        available,
      });
      continue;
    }

    const productCode = info.productCode;
    // productDescription holds the customer-friendly text; fall back to the code.
    const productDescription = info.productDescription || productCode;
    rows.push({ productId, productCode, productDescription, available });
  }

  rows.sort((a, b) =>
    a.productDescription.localeCompare(b.productDescription, "en-GB", {
      sensitivity: "base",
    })
  );
  unmatched.sort((a, b) => b.available - a.available);

  return { rows, unmatched };
}

export function filterCustomerStockRows(rows, search) {
  const query = asText(search).toLowerCase();
  if (!query) return rows || [];
  return (rows || []).filter(
    (row) =>
      row.productDescription.toLowerCase().includes(query) ||
      row.productCode.toLowerCase().includes(query)
  );
}

export function totalAvailable(rows) {
  return (rows || []).reduce(
    (sum, row) => sum + (Number(row.available) || 0),
    0
  );
}

export function reportDateLabel(date = new Date()) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function fileStamp(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

export function exportCustomerStockExcel(rows, generatedAt = new Date()) {
  const data = (rows || []).map((row) => ({
    Product: row.productDescription,
    "Product Code": row.productCode,
    Available: row.available,
  }));
  const worksheet = XLSX.utils.json_to_sheet(
    data.length ? data : [{ Product: "", "Product Code": "", Available: "" }]
  );
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Stock");
  XLSX.writeFile(workbook, `stock-availability-${fileStamp(generatedAt)}.xlsx`);
}

// Pure spec for src/pdf/pdfCore.js (kept import-free so it stays unit-testable).
export function buildCustomerStockPdfSpec(rows, generatedAt = new Date()) {
  const list = rows || [];
  return {
    title: "Cylinder Stock Availability",
    subtitles: [
      reportDateLabel(generatedAt),
      `${list.length} product${list.length === 1 ? "" : "s"} currently in stock`,
    ],
    sections: [
      {
        title: "Currently available",
        table: {
          rows: [
            ["Product", "Product Code", "Available"],
            ...list.map((row) => [
              row.productDescription,
              row.productCode,
              row.available,
            ]),
          ],
        },
      },
    ],
    fileName: `stock-availability-${fileStamp(generatedAt)}.pdf`,
  };
}
